import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  SharedDataService,
  PafListsService,
  PS_PAFX_DR_Result,
  PS_PAFX_Fournisseur_Result,
  PafxFinancesFilters,
  PS_PAFX_Finances_Result,
  Family
} from 'ngx-paf-common';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import moment from 'moment';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { FinancesService } from 'localServices/finances.service';

import {
  filterBy,
  CompositeFilterDescriptor,
  process,
  State,
  DataResult,
} from '@progress/kendo-data-query';

import {
  DataStateChangeEvent,
  GridComponent,
} from "@progress/kendo-angular-grid";

import {ExcelExportComponent, ExcelExportData, WorkbookSheetColumn, WorkbookSheetRow } from '@progress/kendo-angular-excel-export';

type PS_PAFX_Finances_Extend = PS_PAFX_Finances_Result &
{
  isESAT?: boolean,
  isEA?: boolean,
  isTIH?: boolean,
  isRayonnement?: boolean,
  isPanelReg?: boolean,
  Detail3seExcel?: string,
  Part_De_La_DR_Excel?: number | null
}

@Component({
  selector: 'finances',
  templateUrl: './finances.component.html',
  styleUrls: ['./finances.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,  //Permet d'utiliser le style redefini pour la grille dans style.scss et non le style global Telerik
})
export class FinancesComponent implements OnInit, OnDestroy {

  constructor(
    public sharedDataSvc: SharedDataService,
    private financesSvc: FinancesService,
    private pafLstsSvc: PafListsService) { }


  @ViewChild("cboPoles") public cboPoles: MultiSelectComponent;
  @ViewChild("cboRegs") public cboRegs: MultiSelectComponent;
  @ViewChild("cboEtbs") public cboEtbs: MultiSelectComponent;
  @ViewChild("cboTakSegments") public cboTakSegments: MultiSelectComponent;
  @ViewChild("cboTakSubtyps") public cboTakSubtyps: MultiSelectComponent;
  @ViewChild("cboTakSubtyps2") public cboTakSubtyps2: MultiSelectComponent;

  filters: PafxFinancesFilters = new PafxFinancesFilters();
  visibleFilters: boolean = true;
  lstPAfFamilies: Family[] = [];

  lstDonnees: any[] = [
    { Key: 'FAC', Value: 'Factures (date de facture)' },
    { Key: 'CTA', Value: 'Factures (date comptable)' },
    { Key: 'CDE', Value: 'Commandes' }
  ]

  lstPresentations: any[] = [
    { Key: 'GRP', Value: 'Groupes', Ps: 'PS_PV2_Indicateurs_Grp' },
    { Key: 'TIE', Value: 'Fournisseurs', PS: 'PS_PV2_Indicateurs_Tie' },
    { Key: 'ETB', Value: 'Fournisseurs part par établissement', PS: 'PS_PV2_Indicateurs_Tie_Etb' }
  ]

  lstEtablissements: PS_PAFX_DR_Result[] = [];

  //Axe 1
  get lstPoles(): PS_PAFX_DR_Result[] {
    return this.lstEtablissements.filter(item => !!item.PolCod).distinctValues('PolCod');
  }

  //Axe 2
  get lstRegs(): PS_PAFX_DR_Result[] {
    return this.lstEtablissements
      .filter(r => (this.filters.PolCods || []).length == 0 || this.filters.PolCods.some(p => p == null) || this.filters.PolCods.some(p => p == r.PolCod))
      .distinctValues('RegCod');
  };

  get lstEtbs(): PS_PAFX_DR_Result[] {
    return this.lstEtablissements
      .filter(item => (this.filters.PolCods || []).length == 0 || this.filters.PolCods.some(p => p == item.PolCod || p == null))
      .filter(item => (this.filters.RegCods || []).length == 0 || this.filters.RegCods.some(r => r == item.RegCod || r == null))
      .distinctValues('EtbCod');
  }

  get lstSegments(): string[] {
    return this.lstPAfFamilies.map(item => item.TakFamily).distinct();
  }

  get lstSubTyps(): string[] {
    return this.lstPAfFamilies
      .filter(item => (this.filters.Families || []).length == 0 || this.filters.Families.some(f => f == item.TakFamily))
      .map(item => item.TakSubTyp).distinct();
  }

  get lstSubTyp2s(): string[] {
    return this.lstPAfFamilies
      .filter(item => (this.filters.Families || []).length == 0 || this.filters.Families.some(f => f == item.TakFamily))
      .filter(item => (this.filters.SubTyps || []).length == 0 || this.filters.SubTyps.some(s => s == item.TakSubTyp))
      .map(item => item.TakSubTyp2).distinct();
  }

  /**
   * Appel lié au bouton de recherche pour la
   */
  public search(): void {
    this.sharedDataSvc.WindowLock();
    this.presentation = this.filters.Presentation;
    this.getDatas()
      .subscribe({
        next: res => {
          this.sharedDataSvc.WindowUnlock();
        },
        error: (response: any) => {
          this.sharedDataSvc.WindowUnlock();
          this.sharedDataSvc.alert(response.error.Message || response.error);
        }
      });
  }
  
  /**
   * Recupération dela liste des éléments qui alimenteront la grille
   * @returns Observable<PS_PAFX_Finances_Extend[]>
   */
  getDatas(): Observable<PS_PAFX_Finances_Extend[]> {
    return this.financesSvc.Search(this.filters)
      .pipe(tap((results: PS_PAFX_Finances_Result[]) => {
        this.datas = results.map(item => this.ExtendResult(item));
        this.state.skip = 0;
        this.datasLoaded = true;
        this.bindDatasToGrid();
      }));
  }


  ExtendResult(item:PS_PAFX_Finances_Result):PS_PAFX_Finances_Extend {
    return {
          ...item,
          isESAT:(item.TopESAT == 1),
          isEA:(item.TopEA == 1),
          isTIH:(item.TopTIH == 1),
          isRayonnement:(item.TopRayonnement == 1),
          isPanelReg: (item.TopPanelReg==1),
          Detail3seExcel: item.Detail3se ? item.Detail3se.split('|').join('; ') : '',
          Part_De_La_DR_Excel: item.Part_De_La_DR ? item.Part_De_La_DR / 100 : null
        };
  }

  lstFournisseurs: PS_PAFX_Fournisseur_Result[] = [];
  profilChangeSub: Subscription = null;

  ngOnInit(): void {
    if (this.sharedDataSvc.profilLoaded) {
      this.checkAccessTakapa();
    }
    this.profilChangeSub = this.sharedDataSvc.loggedProfilChange.subscribe(p => {
      this.checkAccessTakapa();
    });
    forkJoin([
      this.financesSvc.Drs(),
      this.pafLstsSvc.getFamilies()
    ]).subscribe(results => {
      this.lstEtablissements = results[0];//.filter(p=>p.PolCod!='*' && p.RegCod!='*' && p.SocCod!='*' && p.EtbCod!='*');
      this.lstPAfFamilies = results[1];
      this.reinitAll();
    });
  }

  ngOnDestroy(): void {
    this.profilChangeSub.unsubscribe();
  }

  public reinit() {
    this.filters = new PafxFinancesFilters();
    this.filters.LngCod = this.sharedDataSvc.lngCod;
    this.filters.MmDe = '01';
    this.filters.AaaaDe = moment().format('YYYY');
    this.filters.MmA = moment().format('MM')
    this.filters.AaaaA = moment().format('YYYY');
    this.filters.FacCdeCta = 'FAC';
    this.filters.Presentation = 'GRP';
    this.supplierInput = null;
    this.onPolesFilterChanged('');
    this.onRegsFilterChanged('');
    this.onEtbsFilterChanged('');
    this.onFamilyFilterChanged('');
    this.onSubTypFilterChanged('');
    this.onSubTyp2FilterChanged('');
  }


  public reinitAll() {
    this.reinit();
    this.resetFilters();
  }

  checkAccessTakapa(): void {
    if (!this.sharedDataSvc.checkProfilAccessPafSettings('TAKAPA')) {
      this.sharedDataSvc.alert('Votre profil ne vous autorise pas à accéder à cet écran'
        , () => {
          if (this.profilChangeSub != null) this.profilChangeSub.unsubscribe();
          this.sharedDataSvc.redirectToDefaultScreen();
        });
    }
  }


  displayEtb(item: PS_PAFX_DR_Result): string {
    return item.SocCod + ' ' + item.EtbLib;
  }


  toggleDisplayFilters(): void {
    this.visibleFilters = !this.visibleFilters;
  }


  get gridHeight(): number {
    return this.visibleFilters ? 500 : 670;
  }

  public highlightSearch(display: string, search: string): string {
    if (!display || !search) return display;
    let startIndex: number = display.toLowerCase().removeAccents().indexOf(search.toLowerCase().removeAccents());
    if (startIndex < 0) return display;
    let endIndex = startIndex + search.length;
    return [
      display.slice(0, startIndex),
      '<mark>',
      display.slice(startIndex, endIndex),
      '</mark>',
      display.slice(endIndex)
    ].join('');
  }


  searchSuppliersSubscription: Subscription = null;


  public searchSuppliers2(mask: string) {
    if ((mask || '').length < 3) {
      this.lstFournisseurs = [];
      return;
    }
    if (!this.searchSuppliersSubscription) {
      this.searchSuppliersSubscription = this.financesSvc.Fournisseurs(mask)
        .subscribe(res => {
          this.lstFournisseurs = res;
          this.lstFournisseurs.forEach(r => (<any>r).Display = r.FOU_Code + ' - ' + r.FOU_Nom);
          setTimeout(() => { this.searchSuppliersSubscription = null }, 200);
        });
    }

  }

  isSearchingSuppliers: boolean = false;
  public searchSuppliers = (text$: Observable<string>) => {
    return text$.pipe(
      debounceTime(200),
      tap(() => this.isSearchingSuppliers = true),
      distinctUntilChanged(),
      switchMap((term: string) => {
        return (term.length < 2 ?
          of(<PS_PAFX_Fournisseur_Result[]>[])
          : this.financesSvc.Fournisseurs(term)
            .pipe(
              map(results => results.length ? results : [{ FOU_Code: term || '' }])
            ));
      }),
      tap(() => this.isSearchingSuppliers = false)
    );
  }
  supplierResultFormatter = (x: PS_PAFX_Fournisseur_Result) => {
    if (x) {
      return (x.FOU_Code || '') + (x.FOU_Nom ? ' - ' + x.FOU_Nom : '');
    } else {
      return '';
    }
  };


  isSearchingGroups: boolean = false;
  public searchGroups = (text$: Observable<string>) => {
    return text$.pipe(
      debounceTime(200),
      tap(() => this.isSearchingGroups = true),
      distinctUntilChanged(),
      switchMap((term: string) => {
        return (term.length < 2 ?
          of(<string[]>[])
          : this.financesSvc.Groupes(term)
            .pipe(
              map(results => results.length ? results : [term || ''])
            ));
      }),
      tap(() => this.isSearchingGroups = false)
    );
  }

  get excelDatas(): Observable<any[]> {
    if ((this.datas || []).length > 0 || this.datasLoaded) {
      return of(this.datas);
    } else {
      return this.getDatas();
    }
  }



  public exportExcel() {
    this.sharedDataSvc.WindowLock();
    this.excelDatas.subscribe(xDatas => {
      let component: ExcelExportComponent = this.excelExportComponent;
      component.data = xDatas;
      const options = component.workbookOptions();
      let header: WorkbookSheetRow = options.sheets[0].rows.find(r => r.type == 'header');
      if (header) {
        const cols = options.sheets[0].columns;
        cols.forEach((col: WorkbookSheetColumn, i: number) => {
          col.autoWidth = true;
        });
      }
      component.save(options);
      // Vu que l'export répond lentement, on retarde la fin du sablier d'1s par millier d'enregistrement
      setTimeout(() => {
        this.sharedDataSvc.WindowUnlock();
      }, xDatas.length);
    });
  }


  poleSearchMask: string;
  filteredPoles: PS_PAFX_DR_Result[] = [];
  onPolesFilterChanged(filter: string): void {
    this.poleSearchMask = filter;
    if (!filter) {
      this.filteredPoles = this.lstPoles;
    } else {
      const lowerFilter = filter.toLowerCase();
      this.filteredPoles = this.lstPoles
        .filter(p => p.PolLib.toLowerCase().includes(lowerFilter));
    }
    this.filteredPoles.sortObject('p.PolLib asc');
    this.onRegsFilterChanged('')
  }

  regSearchMask: string;
  filteredRegs: PS_PAFX_DR_Result[] = [];
  onRegsFilterChanged(filter: string): void {
    this.regSearchMask = filter;
    if (!filter) {
      this.filteredRegs = this.lstRegs;
    } else {
      const lowerFilter = filter.toLowerCase();
      this.filteredRegs = this.lstRegs
        .filter(r => r.RegLib.toLowerCase().includes(lowerFilter));
    }
    this.filteredRegs.sortObject('r.RegLib asc');
    this.onEtbsFilterChanged('');
  }

  etbSearchMask: string;
  filteredEtbs: PS_PAFX_DR_Result[] = [];
  onEtbsFilterChanged(filter: string): void {
    this.etbSearchMask = filter;
    if (!filter) {
      this.filteredEtbs = this.lstEtbs;
    } else {
      const lowerFilter = filter.toLowerCase();
      this.filteredEtbs = this.lstEtbs
        .filter(e => this.displayEtb(e).toLowerCase().includes(lowerFilter));
    }
    this.filteredEtbs.sort((e1, e2) => {
      let v1 = this.displayEtb(e1).toLowerCase();
      let v2 = this.displayEtb(e2).toLowerCase();
      if (v1 < v2) return -1;
      if (v1 > v2) return 1;
      return 0;
    });
  }

  takFamilySearchMask: string;
  filteredSegments: string[] = [];
  onFamilyFilterChanged(filter: string): void {
    this.takFamilySearchMask = filter;
    if (!filter) {
      this.filteredSegments = this.lstSegments;
    } else {
      const lowerFilter = filter.toLowerCase();
      this.filteredSegments = this.lstSegments
        .filter(s => (!!s) && s.toLowerCase().includes(lowerFilter));
    }
    this.filteredSegments.sort();
    this.onSubTypFilterChanged(''); 
  }

  takSubTypSearchMask: string;
  filteredSubTyps: string[] = [];
  onSubTypFilterChanged(filter: string): void {
    this.takSubTypSearchMask = filter;
    if (!filter) {
      this.filteredSubTyps = this.lstSubTyps;
    } else {
      const lowerFilter = filter.toLowerCase();
      this.filteredSubTyps = this.lstSubTyps
        .filter(s => (!!s) && s.toLowerCase().includes(lowerFilter));
    }
    this.filteredSubTyps.sort();
    this.onSubTyp2FilterChanged('');
  }

  filteredSubTyp2s: string[] = [];
  takSubTyp2SearchMask: string;
  onSubTyp2FilterChanged(filter: string): void {
    this.takSubTyp2SearchMask = filter;
    if (!filter) {
      this.filteredSubTyp2s = this.lstSubTyp2s;
    } else {
      const lowerFilter = filter.toLowerCase();
      this.filteredSubTyp2s = this.lstSubTyp2s
        .filter(s => (!!s) && s.toLowerCase().includes(lowerFilter));
    }
    this.filteredSubTyp2s.sort(); 
  }

  datas?: PS_PAFX_Finances_Result[];
  datasLoaded: boolean = false;
  gridData?: DataResult;
  @ViewChild('excelexport') excelExportComponent: ExcelExportComponent;
  @ViewChild('financesGrid') grid: GridComponent;
  excelExportFileName: string = 'Finance-' + moment().format('YYYYMMDD-HHmm') + '.xlsx';
  public state: State = {};

  public presentation: string = '';


  filteredDatas: PS_PAFX_Finances_Result[] = [];


  clear(): void {
    this.datas = [];
    this.gridData.data = [];
    this.gridData.total = 0;
  }


  public gridPageableSettings: any = {
    pageSizes: [10, 20, 50, 100],
    position: 'top'
  }

  public resetFilters(): void {
    this.state = {
      skip: 0,
      take: 100,
    };
    this.datas = [];
    this.bindDatasToGrid();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.state.filter = filter;
    this.bindDatasToGrid();
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.state = state;
    this.bindDatasToGrid();
  }


  public bindDatasToGrid() {
    this.filteredDatas = filterBy(this.datas, this.state.filter);
    this.gridData = process(this.datas, this.state);
  }


  public getMontantTotal(): number {
    return this.filteredDatas.map(d => d.MtHt).reduce((a, b) => a + b, 0);
  }

  public getTotalFactures(): number {
    return this.filteredDatas.map(d => d.Nb_Factures).reduce((a, b) => a + b, 0);
  }

  public indicateurClass(indicateurCod: string) {
    switch ((indicateurCod || '').toLowerCase()) {
      case 'vert':
        return 'indicateur indicateur-vert';
      case 'orange':
        return 'indicateur indicateur-orange';
      case 'rouge':
        return 'indicateur indicateur-rouge';
      default:
        return 'indicateur indicateur-nc';
    }
  }

  checkNgClass(fieldValue: number): any {
    return {
      'far': true,
      'fa-check-square': (fieldValue == 1),
      'fa-square': (fieldValue != 1),
    }
  }

  public convertTooltip(input: string): string {
    return (input || '').split('|').join('\n');
  }

  public getSupplierLink(item: any): string {
    if (!(item || {}).SupplierId) return '';
    return '/supplier-details/' + item.SupplierId + '/takapa';
  }

  private _supplierInput: any = null;
  get supplierInput(): any {
    return this._supplierInput;
  }
  set supplierInput(value: any) {
    this._supplierInput = value;
    if (value && typeof value === 'object') {
      // Cas sélection d’un objet dans la liste
      this.filters.TieCod = value.FOU_Code;
    } else {
      // Cas saisie manuelle ou effacement
      this.filters.TieCod = null;
    }
  };

  onSupplierBlur() {
    // Si l'utilisateur a tapé un texte qui ne correspond à rien → on vide
    if (!this._supplierInput || this._supplierInput && typeof this._supplierInput === 'string') {
      this.supplierInput = null;
    }
  }

  get selectedPols(): PS_PAFX_DR_Result[] {
    return this.lstEtablissements.filter(item => this.filters.PolCods.indexOf(item.PolCod) >= 0);
  }

  get selectedRegs(): PS_PAFX_DR_Result[] {
    return this.lstEtablissements.filter(item => this.filters.RegCods.indexOf(item.RegCod) >= 0);
  }

  get selectedEtbs(): PS_PAFX_DR_Result[] {
    return this.lstEtablissements.filter(item => this.filters.EtbCods.indexOf(item.EtbCod) >= 0);
  }

  get selectedSegments(): Family[] {
    return this.lstPAfFamilies.filter(item => this.filters.Families.indexOf(item.TakFamily) >= 0);
  }
  get selectedFamilies(): Family[] {
    return this.lstPAfFamilies.filter(item => this.filters.SubTyps.indexOf(item.TakSubTyp) >= 0);
  }
  get selectedSubFamilies(): Family[] {
    return this.lstPAfFamilies.filter(item => this.filters.SubTyp2s.indexOf(item.TakSubTyp2) >= 0);
  }

  public onAxeChanged(values: string[], filterName: string) {
    if (!values) values = [];
    switch (filterName) {
      case 'PolCods':
        this.filters.RegCods = this.selectedRegs
          .filter(item => (this.selectedPols || []).length == 0 || this.selectedPols.some(pol => item.PolCod == pol.PolCod))
          .map(item => item.RegCod).distinct();
        this.onAxeChanged(this.filters.RegCods, 'RegCods');
        this.onRegsFilterChanged('');
        break;
      case 'RegCods':
        this.filters.EtbCods = this.selectedEtbs
          .filter(item => (this.selectedPols || []).length == 0 || this.selectedPols.some(pol => item.PolCod == pol.PolCod))
          .filter(item => (this.selectedRegs || []).length == 0 || this.selectedRegs.some(reg=>reg.RegCod==item.RegCod))
          .map(item => item.EtbCod).distinct();
        this.onEtbsFilterChanged(''); 
        break;
    }
  }


  public onFamilyChange(values: string[], filterName: string) {
    if (!values) values = [];
    switch (filterName) {
      case 'Segments':
        this.filters.SubTyps = this.selectedFamilies
          .filter(item => this.selectedSegments.length==0 || this.selectedSegments.some(seg => item.TakFamily == seg.TakFamily))
          .map(item => item.TakSubTyp).distinct();
        this.onFamilyChange(this.filters.SubTyps, 'Families');
        this.onSubTypFilterChanged('');
        break;
      case 'Families':
        this.filters.SubTyp2s = this.selectedSubFamilies
          .filter(item => this.selectedSegments.length==0 || this.selectedSegments.some(seg => item.TakFamily == seg.TakFamily))
          .filter(item => (this.selectedFamilies || []).length == 0 || this.selectedFamilies.some(fam=>item.TakSubTyp==fam.TakSubTyp))
          .map(item => item.TakSubTyp2).distinct();
        this.onSubTyp2FilterChanged('');
        break;
    }
  }

  



}